import {
  COST_BANDS,
  FOOD_DIET_CLASSES,
  FOOD_KINDS,
  FOOD_SOURCES,
  FOOD_VIEWS,
  MEAL_ROLES,
  PREP_TIME_BANDS,
  type AdminFoodListItem,
  type FoodView,
} from '@steado/admin-contract/foods';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ColumnsMenu } from '@/components/table/ColumnsMenu';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { ChipsCell, RowActionsCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { humanise } from '@/lib/format';
import { cn } from '@/lib/utils';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { toggleToken } from '@/lib/toggle-token';

import { BulkVerifyDialog, type BulkOutcome } from './BulkVerifyDialog';
import { OPTIONAL_FOOD_COLUMNS, OPTIONAL_FOOD_COLUMN_KEYS, optionalFoodCell } from './foods-columns';
import { FOODS_PAGE_SIZE, foodsQueryOptions, verifyFood } from './foods-query';
import { viewDefaults, type FoodsSearch } from './foods-search';

/**
 * The design's vocabulary. Macros fold into the food's own cell as a sub-line
 * and the two state chips become "Still to curate", because what an operator
 * scans this table for is what is left to do, not what each row already is.
 */
const BASE_COLUMNS: readonly TableColumn[] = [
  { key: 'food', label: 'Food', width: '2fr' },
  { key: 'diet', label: 'Diet', width: '1fr' },
  { key: 'source', label: 'Where it came from', width: '1.4fr' },
  { key: 'curate', label: 'Still to curate', width: '1.2fr' },
];

const ACTIONS_COLUMN: TableColumn = { key: 'actions', label: '', width: '132px', align: 'right' };

/**
 * What a row is still missing, worst first.
 *
 * Only absences: a verified row with a portion and a meal role contributes
 * nothing, which is the point — an empty cell here means "nothing to do", and
 * that is the fastest thing to scan a catalog for.
 */
function todo(food: AdminFoodListItem): string[] {
  return [
    // Not a curation gap, but the one row state that changes what every other
    // cell means: a deactivated food reaches no plan at all, so its missing
    // portion is not work anybody should do. It leads for that reason.
    ...(food.isActive ? [] : ['deactivated']),
    ...(food.isVerified ? [] : ['unverified']),
    ...(food.portionCount === 0 ? ['no portion'] : []),
    ...(food.mealRole === null ? ['no meal role'] : []),
    ...(food.cuisines.length === 0 ? ['no cuisine'] : []),
  ];
}

const VIEW_LABELS: Readonly<Record<FoodView, string>> = {
  catalog: 'Catalog',
  review: 'Review queue',
};

export interface FoodsScreenProps {
  readonly search: FoodsSearch;
  readonly onSearchChange: (next: FoodsSearch) => void;
  /** Navigating is the Route's job, so this screen stays testable without a router. */
  readonly onNewFood: () => void;
  readonly onOpenFood: (foodId: string) => void;
}

export function FoodsScreen({ search, onSearchChange, onNewFood, onOpenFood }: FoodsScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  /*
   * Selection is component state, not URL state, unlike every filter here. A
   * link to "these nineteen foods" is not a view of the catalog, and pasting
   * one would hand somebody a bulk action they did not choose.
   */
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [bulkAction, setBulkAction] = useState<'verify' | null>(null);
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);

  const query = useQuery(foodsQueryOptions(api, search));
  const counts = useQuery(scopeCountsQueryOptions(api));

  const activeColumns = useMemo(
    () => OPTIONAL_FOOD_COLUMNS.filter((column) => (search.cols ?? []).includes(column.key)),
    [search.cols],
  );
  const columns = useMemo(() => [...BASE_COLUMNS, ...activeColumns, ACTIONS_COLUMN], [activeColumns]);

  const setFilter = useCallback(
    (patch: Partial<FoodsSearch>) => {
      // Any filter change invalidates the paging position.
      setCursorStack([]);
      onSearchChange({ ...search, ...patch, cursor: undefined });
    },
    [search, onSearchChange],
  );

  const onSelectView = useCallback(
    (view: FoodView) => {
      setCursorStack([]);
      // The view sets the starting filters; it does not lock them, so an
      // operator can widen the queue from inside it.
      onSearchChange({ ...search, view, ...viewDefaults(view), cursor: undefined });
    },
    [search, onSearchChange],
  );

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, search.cursor]);
    onSearchChange({ ...search, cursor: next });
  }, [query.data, search, onSearchChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onSearchChange({ ...search, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [search, onSearchChange]);

  const filterSpecs: readonly FilterSpec[] = useMemo(
    () => [
      {
        id: 'kind',
        label: 'Kind',
        value: search.kind,
        options: FOOD_KINDS.map((kind) => ({ value: kind, label: kind })),
        onChange: (kind) => {
          setFilter({ kind: kind as FoodsSearch['kind'] });
        },
      },
      {
        id: 'source',
        label: 'Source',
        value: search.source,
        options: FOOD_SOURCES.map((source) => ({ value: source, label: humanise(source) })),
        onChange: (source) => {
          setFilter({ source: source as FoodsSearch['source'] });
        },
      },
      {
        id: 'dietClass',
        label: 'Diet class',
        value: search.dietClass,
        options: FOOD_DIET_CLASSES.map((diet) => ({ value: diet, label: humanise(diet) })),
        onChange: (dietClass) => {
          setFilter({ dietClass: dietClass as FoodsSearch['dietClass'] });
        },
      },
      {
        // Catalog Intelligence's curated bands. Read-only facts here — edited
        // on a food's record or pushed with catalog:push, never a form on
        // this screen — so the filter's only job is triage: which
        // rows still need a curator's eyes.
        id: 'mealRole',
        label: 'Meal role',
        value: search.mealRole,
        options: MEAL_ROLES.map((role) => ({ value: role, label: humanise(role) })),
        onChange: (mealRole) => {
          setFilter({ mealRole: mealRole as FoodsSearch['mealRole'] });
        },
      },
      {
        id: 'prepTimeBand',
        label: 'Prep time',
        value: search.prepTimeBand,
        options: PREP_TIME_BANDS.map((band) => ({ value: band, label: humanise(band) })),
        onChange: (prepTimeBand) => {
          setFilter({ prepTimeBand: prepTimeBand as FoodsSearch['prepTimeBand'] });
        },
      },
      {
        id: 'costBand',
        label: 'Cost',
        value: search.costBand,
        options: COST_BANDS.map((band) => ({ value: band, label: humanise(band) })),
        onChange: (costBand) => {
          setFilter({ costBand: costBand as FoodsSearch['costBand'] });
        },
      },
      {
        id: 'verification',
        label: 'Verification',
        value: search.verification,
        options: [
          { value: 'verified', label: 'verified' },
          { value: 'unverified', label: 'unverified' },
        ],
        onChange: (verification) => {
          setFilter({ verification: verification as FoodsSearch['verification'] });
        },
      },
      {
        id: 'activity',
        label: 'State',
        value: search.activity,
        options: [
          { value: 'active', label: 'active' },
          { value: 'inactive', label: 'deactivated' },
        ],
        onChange: (activity) => {
          setFilter({ activity: activity as FoodsSearch['activity'] });
        },
      },
      {
        // 'without' is the curation queue: an ingredient enters the plan
        // shortlist only once it has a household measure, so these are the
        // rows a portion would promote.
        id: 'householdPortion',
        label: 'Household portion',
        value: search.householdPortion,
        options: [
          { value: 'with', label: 'has one' },
          { value: 'without', label: 'needs one' },
        ],
        onChange: (householdPortion) => {
          setFilter({ householdPortion: householdPortion as FoodsSearch['householdPortion'] });
        },
      },
    ],
    [search, setFilter],
  );

  // The selected photo-scan estimates and their listed diet class, which a
  // verify has to confirm (see BulkVerifyDialog). Selection is per page.
  const selectedEstimates = useMemo(
    () =>
      new Map(
        (query.data?.items ?? [])
          .filter((food) => food.source === 'ai_estimated' && selectedIds.includes(food.id))
          .map((food) => [food.id, food.dietClass] as const),
      ),
    [query.data, selectedIds],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((food) => ({
        id: food.id,
        onOpen: () => {
          onOpenFood(food.id);
        },
        cells: [
          <TextCell
            key="food"
            value={food.name}
            sub={`${humanise(food.kind)} · ${String(food.macrosPer100g.kcal)} kcal · ${String(food.macrosPer100g.proteinG)}P ${String(food.macrosPer100g.carbsG)}C ${String(food.macrosPer100g.fatG)}F`}
            weight="medium"
          />,
          <ChipsCell
            key="diet"
            items={[humanise(food.dietClass), ...(food.hasOnionGarlic ? ['og'] : [])]}
            max={2}
          />,
          <div key="source" className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-[13px] text-foreground">{humanise(food.source)}</span>
            {/*
              The allergen provenance, not the import date. "Contains none of
              the six" and "nobody has ever looked" were spelled the same way
              until Unit 8, which is how every INDB dish disappeared from the
              shortlist of anyone with an allergy — so an unassessed row says
              so here, in warn ink, rather than one screen deeper.
            */}
            <span
              className={cn(
                'truncate text-xs',
                food.allergenSource === 'unassessed' ? 'text-warn-fg' : 'text-muted-foreground',
              )}
            >
              {food.allergenSource === 'unassessed'
                ? 'allergens unassessed'
                : `allergens ${humanise(food.allergenSource)}`}
            </span>
          </div>,
          <ChipsCell key="curate" items={todo(food)} max={3} />,
          ...activeColumns.map((column) => {
            const cell = optionalFoodCell(column.key, food);

            return (
              <TextCell
                key={column.key}
                value={cell.value}
                mono={cell.mono}
                align={column.align ?? 'left'}
                {...(cell.dim ? { tone: 'muted' as const } : {})}
              />
            );
          }),
          <RowActionsCell key="actions">
            {food.isVerified || !can.mutate ? null : (
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  setSelectedIds([food.id]);
                  setBulkAction('verify');
                }}
              >
                Verify
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-xs"
              title="Open and edit"
              aria-label={`Open ${food.name}`}
              onClick={() => {
                onOpenFood(food.id);
              }}
            >
              <Pencil aria-hidden="true" className="size-3.5" />
            </Button>
          </RowActionsCell>,
        ],
      })),
    [query.data, onOpenFood, activeColumns, can.mutate],
  );

  const isFiltered =
    search.search !== undefined ||
    search.kind !== undefined ||
    search.source !== undefined ||
    search.dietClass !== undefined ||
    search.mealRole !== undefined ||
    search.prepTimeBand !== undefined ||
    search.costBand !== undefined ||
    search.verification !== undefined ||
    search.activity !== undefined ||
    search.householdPortion !== undefined;

  const onReset = useCallback(() => {
    setCursorStack([]);
    onSearchChange({ view: search.view, ...viewDefaults(search.view) });
  }, [search.view, onSearchChange]);

  const pageOffset = cursorStack.length * FOODS_PAGE_SIZE;

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load the catalog"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1300px] flex-col gap-3">
      <div className="flex flex-wrap items-center gap-[9px]">
        <ScopePills
          label="Catalog view"
          scopes={FOOD_VIEWS.map((view) => ({
            id: view,
            label: VIEW_LABELS[view],
            ...(counts.data === undefined ? {} : { count: counts.data.foods[view] }),
          }))}
          value={search.view}
          onChange={onSelectView}
        />

        <span className="ml-auto flex items-center gap-[9px]">
          <ColumnsMenu
            columns={OPTIONAL_FOOD_COLUMNS}
            active={search.cols ?? []}
            onToggle={(key) => {
              onSearchChange({
                ...search,
                cols: toggleToken(search.cols ?? [], key, OPTIONAL_FOOD_COLUMN_KEYS),
              });
            }}
          />
          <Button
            type="button"
            size="sm"
            onClick={onNewFood}
            disabled={!can.mutate}
            title={can.mutate ? undefined : 'Creating a food requires the admin role.'}
          >
            <Plus aria-hidden="true" />
            New food
          </Button>
        </span>
      </div>

      {/*
        Kept from before the redesign, which drew no prose here. The review
        queue's ordering is not self-evident from the table — oldest first
        looks like a bug until you know it is a queue — and one sentence is
        cheaper than an operator re-sorting it.
      */}
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        {search.view === 'review'
          ? 'Unverified rows the decomposition tier estimated, oldest first, so the one waiting longest is next. Verifying promotes the numbers from “estimated” to plain in the app.'
          : 'Every food a plan or a log can reference. Open a row to edit its household portions, cuisines and how eagerly plans offer it.'}
      </p>

      <TableToolbar
        searchValue={search.search ?? ''}
        searchPlaceholder="Name or alias, any language"
        onSearchChange={(value) => {
          setFilter({ search: value === '' ? undefined : value });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
      />

      <DataTable
        caption="Foods"
        title={VIEW_LABELS[search.view]}
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={columns}
        rows={rows}
        isLoading={query.isPending}
        {...(can.mutate
          ? {
              selection: {
                selectedIds,
                itemLabel: 'Select this food',
                onToggle: (id: string) => {
                  setSelectedIds((current) =>
                    current.includes(id) ? current.filter((candidate) => candidate !== id) : [...current, id],
                  );
                },
                onToggleAll: () => {
                  setSelectedIds((current) =>
                    current.length === rows.length ? [] : rows.map((row) => row.id),
                  );
                },
                actions: (
                  <>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => {
                        setBulkAction('verify');
                      }}
                    >
                      Verify {selectedIds.length}
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        setSelectedIds([]);
                      }}
                    >
                      Clear
                    </Button>
                  </>
                ),
              },
            }
          : {})}
        emptyTitle={search.view === 'review' ? 'Nothing to review' : isFiltered ? 'No matches' : 'No foods'}
        emptyBody={
          search.view === 'review'
            ? 'Every estimated food has been checked. New ones appear here as the scan pipeline creates them.'
            : isFiltered
              ? 'No food matches the current search and filters. Clear them to see the whole catalog.'
              : 'The catalog is empty. Run the IFCT or INDB import.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={query.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={query.isPending}
          />
        }
      />

      {bulkAction === null ? null : (
        <BulkVerifyDialog
          ids={selectedIds}
          estimates={selectedEstimates}
          onRun={(id, jainExcluded, dietClass) => verifyFood(api, id, jainExcluded, dietClass)}
          onCancel={() => {
            setBulkAction(null);
          }}
          onDone={(outcome) => {
            setBulkAction(null);
            setSelectedIds([]);
            setBulkOutcome(outcome);
            void queryClient.invalidateQueries({ queryKey: ['admin', 'foods'] });
            void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
          }}
        />
      )}

      {/*
        Reported rather than announced and forgotten: the calls are not atomic,
        so which of them failed is the one thing the operator cannot work out
        by looking at the table.
      */}
      {bulkOutcome === null ? null : (
        <Alert
          tone={bulkOutcome.failed.length === 0 ? 'info' : 'warn'}
          onClick={() => {
            setBulkOutcome(null);
          }}
        >
          <AlertDescription>
            {bulkOutcome.failed.length === 0
              ? `Verified ${String(bulkOutcome.succeeded)}.`
              : `Verified ${String(bulkOutcome.succeeded)}; ${String(bulkOutcome.failed.length)} failed — ${bulkOutcome.failed[0]?.message ?? ''}`}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
